/**
 * D23: botão "Acessar" do cartão e da linha de credencial (funções puras, sem React, sem rede).
 *
 * - Com a extensão respondendo ao ping: o clique pede à extensão (`LICTOR_OPEN`) e o usuário cai no
 *   tribunal já logado (ver extensaoLictor.ts).
 * - Sem a extensão (ou com a antiga, que não responde): o clique abre a página de login numa aba nova
 *   e o cartão mostra o aviso para instalar a extensão.
 *
 * Endereço da aba sem a extensão: a `login_url` do catálogo para credencial de tribunal; para
 * "Outro site", o primeiro `url_patterns` em `https://`, sem curinga. A mesma limpeza existe na
 * extensão (extension/src/shared/enderecoDeAcesso.ts); as duas têm de continuar iguais.
 */
import type { BotaoAbrir } from './extensaoLictor';
import { Catalog, findCourt } from './catalog';

/** Texto aprovado pelo Léo na D23: aparece junto do "Acessar" quando a extensão não responde. */
export const AVISO_INSTALAR_EXTENSAO = 'Instale a extensão para entrar já logado';

interface CredencialComEndereco {
  catalog_key?: string | null;
  url_patterns?: string[] | null;
}

/**
 * Um padrão de URL da credencial vira endereço para abrir numa aba.
 * Tira o esquema, o "*." do começo e o "*" ou "/*" do fim; curinga no meio, usuário/senha na
 * URL ou endereço inválido → null. O resultado é sempre `https://` (nunca `http://`).
 */
export function enderecoDoPadrao(padrao: unknown): string | null {
  if (typeof padrao !== 'string') return null;
  const limpo = padrao
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/^\*\./, '')
    .replace(/\/?\*$/, '');
  if (!limpo || limpo.includes('*') || /\s/.test(limpo)) return null;
  try {
    const url = new URL(`https://${limpo}`);
    if (url.protocol !== 'https:' || !url.hostname || url.username || url.password) return null;
    return url.href;
  } catch {
    return null;
  }
}

/** Só endereços https de verdade (a `login_url` do catálogo também passa por aqui). */
function urlHttps(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const lida = new URL(url);
    return lida.protocol === 'https:' && lida.hostname && !lida.username && !lida.password ? lida.href : null;
  } catch {
    return null;
  }
}

/**
 * Página que o "Acessar" abre numa aba nova quando não há extensão: a `login_url` do catálogo
 * (credencial de tribunal, catálogo carregado); senão o primeiro `url_patterns` que vire endereço.
 * null = nada para abrir.
 */
export function urlParaAcessar(credencial: CredencialComEndereco, catalog: Catalog | null): string | null {
  const doCatalogo = urlHttps(findCourt(catalog, credencial.catalog_key)?.login_url);
  if (doCatalogo) return doCatalogo;
  for (const padrao of credencial.url_patterns ?? []) {
    const endereco = enderecoDoPadrao(padrao);
    if (endereco) return endereco;
  }
  return null;
}

/** O "Acessar" aparece em toda credencial com endereço: tribunal do catálogo ou algum `url_patterns` válido. */
export function temBotaoAcessar(credencial: CredencialComEndereco, catalog: Catalog | null): boolean {
  const temTribunal = typeof credencial.catalog_key === 'string' && credencial.catalog_key.length > 0;
  return temTribunal || urlParaAcessar(credencial, catalog) !== null;
}

/**
 * O que o clique em "Acessar" faz:
 * - `extensao`: a extensão respondeu o ping → pede para abrir já logado;
 * - `aba`: sem extensão (ou ainda sem resposta) → abre `url` numa aba nova;
 * - `nada`: sem extensão e sem endereço conhecido (ex.: catálogo ainda não chegou) → botão desabilitado.
 */
export type AcaoDoAcessar = { tipo: 'extensao' } | { tipo: 'aba'; url: string } | { tipo: 'nada' };

export function decidirAcaoDoAcessar(botao: BotaoAbrir | null, url: string | null): AcaoDoAcessar {
  if (botao === 'abrir') return { tipo: 'extensao' };
  return url ? { tipo: 'aba', url } : { tipo: 'nada' };
}

/**
 * Celular/tablet (tela de toque, sem mouse): não existe extensão do Chrome, então o aviso
 * "Instale a extensão…" levaria a uma loja que não funciona ali.
 */
export function ehTelaDeToque(janela: Pick<Window, 'matchMedia'> | undefined = typeof window === 'undefined' ? undefined : window): boolean {
  try {
    return !!janela?.matchMedia?.('(hover: none) and (pointer: coarse)').matches;
  } catch {
    return false;
  }
}

/** O aviso "Instale a extensão…" só aparece quando já se sabe que a extensão não respondeu, e nunca no celular. */
export function mostrarAvisoDaExtensao(botao: BotaoAbrir | null, toque: boolean = ehTelaDeToque()): boolean {
  return botao === 'instalar' && !toque;
}
